import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import {
  Truck,
  Wrench,
  Users,
  Fuel,
  DollarSign,
  TrendingUp,
  Calendar,
  Gauge,
  AlertTriangle,
  Droplets
} from 'lucide-react';
import {
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [consumptionHistory, setConsumptionHistory] = useState(null);
  const [fuelCostHistory, setFuelCostHistory] = useState(null);
  const [hoursHistory, setHoursHistory] = useState([]);
  const [kilometersHistory, setKilometersHistory] = useState([]);
  const [period, setPeriod] = useState('month');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
    fetchHistoricalData();
  }, []);

  useEffect(() => {
    fetchHistoricalData();
  }, [period]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`);
      setStats(response.data);
    } catch (error) {
      toast.error('Error al cargar estadísticas');
    } finally {
      setLoading(false);
    }
  };

  const fetchHistoricalData = async () => {
    try {
      const [consumptionRes, hoursRes, fuelCostRes] = await Promise.all([
        axios.get(`${API}/reports/consumption-history?period=${period}`),
        axios.get(`${API}/reports/machinery-hours-history?period=${period}`),
        axios.get(`${API}/reports/fuel-cost-history?period=${period}`)
      ]);
      setConsumptionHistory(consumptionRes.data);
      setFuelCostHistory(fuelCostRes.data);
      setHoursHistory(hoursRes.data?.hours_data || []);
      setKilometersHistory(hoursRes.data?.kilometers_data || []);
    } catch (error) {
      console.error('Error al cargar datos históricos', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-slate-200 border-t-orange-500 rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm text-slate-500">Cargando dashboard...</p>
        </div>
      </div>
    );
  }

  const totalMachinery = stats?.total_machinery || 0;
  const operationalMachinery = stats?.operational_machinery || 0;
  const outOfService = Math.max(totalMachinery - operationalMachinery, 0);
  const operationalPercent =
    totalMachinery > 0 ? (operationalMachinery / totalMachinery) * 100 : 0;

  const dieselLiters = Number(stats?.monthly_diesel || 0);
  const dieselCost = Number(stats?.monthly_diesel_cost || 0);
  const gasolineLiters = Number(stats?.monthly_gasoline || 0);
  const gasolineCost = Number(stats?.monthly_gasoline_cost || 0);
  const totalFuelCost = Number(
    stats?.monthly_fuel_cost ?? stats?.monthly_cost ?? 0
  );

  const machineryData = [
    {
      name: 'Operacional',
      value: operationalMachinery,
      color: '#22C55E'
    },
    {
      name: 'Fuera de Servicio',
      value: outOfService,
      color: '#EF4444'
    }
  ];

  const formatNumber = (value, decimals = 1) =>
    Number(value || 0).toLocaleString('es-BO', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });

  const formatCurrency = (value) =>
    `Bs. ${Number(value || 0).toLocaleString('es-BO', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;

  const periodLabel =
    period === 'week' ? 'Esta semana' : period === 'month' ? 'Este mes' : 'Este año';

  return (
    <div data-testid="dashboard-page" className="space-y-6 pb-8">

      {/* HEADER */}
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <div className="w-2 h-8 bg-orange-500 rounded-full" />
            <h1
              className="text-3xl md:text-4xl font-bold text-slate-800"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              Centro de Operaciones
            </h1>
          </div>
          <p className="text-slate-500 ml-4">
            Vista general del estado operativo, combustible y productividad.
          </p>
        </div>

        <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-xl px-4 py-2.5 shadow-sm">
          <Calendar size={18} className="text-slate-500" />
          <span className="text-sm text-slate-500">Período:</span>
          <span className="text-sm font-semibold text-slate-800">{periodLabel}</span>
        </div>
      </div>

      {/* KPIs OPERATIVOS */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <div
          data-testid="stat-total-machinery"
          className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Total maquinaria
              </p>
              <p className="text-3xl font-bold text-slate-800 mt-2">{totalMachinery}</p>
              <p className="text-xs text-slate-400 mt-1">Unidades registradas</p>
            </div>
            <div className="p-3 rounded-xl bg-orange-50 text-orange-600">
              <Truck size={25} />
            </div>
          </div>
        </div>

        <div
          data-testid="stat-operational-machinery"
          className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Operacionales
              </p>
              <p className="text-3xl font-bold text-green-600 mt-2">{operationalMachinery}</p>
              <p className="text-xs text-slate-400 mt-1">
                {formatNumber(operationalPercent, 0)}% de la flota
              </p>
            </div>
            <div className="p-3 rounded-xl bg-green-50 text-green-600">
              <Gauge size={25} />
            </div>
          </div>
        </div>

        <div
          data-testid="stat-pending-maintenance"
          className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Mantenimientos
              </p>
              <p className="text-3xl font-bold text-amber-600 mt-2">
                {stats?.pending_maintenance || 0}
              </p>
              <p className="text-xs text-slate-400 mt-1">Pendientes de atención</p>
            </div>
            <div className="p-3 rounded-xl bg-amber-50 text-amber-600">
              <Wrench size={25} />
            </div>
          </div>
        </div>

        <div
          data-testid="stat-total-operators"
          className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Operadores activos
              </p>
              <p className="text-3xl font-bold text-blue-600 mt-2">
                {stats?.total_operators || 0}
              </p>
              <p className="text-xs text-slate-400 mt-1">Personal operativo</p>
            </div>
            <div className="p-3 rounded-xl bg-blue-50 text-blue-600">
              <Users size={25} />
            </div>
          </div>
        </div>
      </div>

      {/* COMBUSTIBLE */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-lg font-bold text-slate-800">Control de combustible</h2>
            <p className="text-xs text-slate-500">
              Cargas registradas durante el período mensual actual.
            </p>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-slate-500">
            <Droplets size={15} />
            Consumo y costos
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

          {/* DIESEL */}
          <div className="bg-white border border-orange-200 rounded-xl overflow-hidden shadow-sm">
            <div className="h-1 bg-orange-500" />
            <div className="p-5">
              <div className="flex items-start justify-between mb-5">
                <div>
                  <span className="inline-flex items-center px-2.5 py-1 rounded-md bg-orange-50 text-orange-700 text-xs font-bold uppercase tracking-wide">
                    Diésel
                  </span>
                  <p className="text-3xl font-bold text-slate-800 mt-3">
                    {formatNumber(dieselLiters)}
                    <span className="text-sm font-medium text-slate-400 ml-1">L</span>
                  </p>
                </div>
                <div className="p-3 bg-orange-50 text-orange-600 rounded-xl">
                  <Fuel size={24} />
                </div>
              </div>
              <div className="pt-3 border-t border-slate-100 flex items-center justify-between">
                <span className="text-xs text-slate-500">Costo</span>
                <span className="font-bold text-slate-800">{formatCurrency(dieselCost)}</span>
              </div>
            </div>
          </div>

          {/* GASOLINA */}
          <div className="bg-white border border-blue-200 rounded-xl overflow-hidden shadow-sm">
            <div className="h-1 bg-blue-500" />
            <div className="p-5">
              <div className="flex items-start justify-between mb-5">
                <div>
                  <span className="inline-flex items-center px-2.5 py-1 rounded-md bg-blue-50 text-blue-700 text-xs font-bold uppercase tracking-wide">
                    Gasolina
                  </span>
                  <p className="text-3xl font-bold text-slate-800 mt-3">
                    {formatNumber(gasolineLiters)}
                    <span className="text-sm font-medium text-slate-400 ml-1">L</span>
                  </p>
                </div>
                <div className="p-3 bg-blue-50 text-blue-600 rounded-xl">
                  <Fuel size={24} />
                </div>
              </div>
              <div className="pt-3 border-t border-slate-100 flex items-center justify-between">
                <span className="text-xs text-slate-500">Costo</span>
                <span className="font-bold text-slate-800">{formatCurrency(gasolineCost)}</span>
              </div>
            </div>
          </div>

          {/* TOTAL */}
          <div className="bg-slate-800 rounded-xl overflow-hidden shadow-sm">
            <div className="h-1 bg-slate-600" />
            <div className="p-5">
              <div className="flex items-start justify-between mb-5">
                <div>
                  <span className="inline-flex items-center px-2.5 py-1 rounded-md bg-white/10 text-slate-200 text-xs font-bold uppercase tracking-wide">
                    Total combustible
                  </span>
                  <p className="text-3xl font-bold text-white mt-3">
                    {formatCurrency(totalFuelCost)}
                  </p>
                </div>
                <div className="p-3 bg-white/10 text-white rounded-xl">
                  <DollarSign size={24} />
                </div>
              </div>
              <div className="pt-3 border-t border-white/10 flex items-center justify-between">
                <span className="text-xs text-slate-400">Volumen total</span>
                <span className="font-bold text-white">
                  {formatNumber(dieselLiters + gasolineLiters)} L
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PERIODO */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-2">
          <Calendar size={19} className="text-slate-500" />
          <div>
            <p className="text-sm font-semibold text-slate-800">Análisis histórico</p>
            <p className="text-xs text-slate-500">Selecciona el período de consulta.</p>
          </div>
        </div>

        <div className="flex gap-1 bg-slate-100 p-1 rounded-lg">
          {['week', 'month', 'year'].map((p) => (
            <button
              key={p}
              data-testid={`period-${p}`}
              onClick={() => setPeriod(p)}
              className={`px-4 py-2 rounded-md text-sm font-semibold transition-all ${
                period === p
                  ? 'bg-white text-orange-600 shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              {p === 'week' ? 'Semana' : p === 'month' ? 'Mes' : 'Año'}
            </button>
          ))}
        </div>
      </div>

      {/* OPERACIÓN + DIÉSEL */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

        {/* ESTADO */}
        <div
          className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm"
          data-testid="machinery-status-chart"
        >
          <div className="flex items-start justify-between mb-2">
            <div>
              <h3 className="text-lg font-bold text-slate-800">Estado de maquinaria</h3>
              <p className="text-xs text-slate-500 mt-1">
                Situación actual de la flota.
              </p>
            </div>
            <div className="p-2 bg-slate-100 rounded-lg text-slate-600">
              <Truck size={19} />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-2 mb-2 mt-4">
            <div className="bg-green-50 rounded-lg p-3">
              <p className="text-xs text-green-700">Operacional</p>
              <p className="text-xl font-bold text-green-700 mt-1">{operationalMachinery}</p>
            </div>
            <div className="bg-red-50 rounded-lg p-3">
              <p className="text-xs text-red-700">Fuera servicio</p>
              <p className="text-xl font-bold text-red-700 mt-1">{outOfService}</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-3">
              <p className="text-xs text-slate-500">Eficiencia</p>
              <p className="text-xl font-bold text-slate-800 mt-1">
                {formatNumber(operationalPercent, 0)}%
              </p>
            </div>
          </div>

          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie
                data={machineryData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, value }) => `${name}: ${value}`}
                outerRadius={75}
                innerRadius={42}
                dataKey="value"
              >
                {machineryData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* DIESEL */}
        <div
          className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm"
          data-testid="diesel-consumption-chart"
        >
          <div className="flex items-start justify-between mb-2">
            <div>
              <h3 className="text-lg font-bold text-slate-800">Consumo de Diésel</h3>
              <p className="text-xs text-slate-500 mt-1">
                Evolución del consumo registrado.
              </p>
            </div>
            <div className="p-2 bg-orange-50 rounded-lg text-orange-600">
              <Fuel size={19} />
            </div>
          </div>

          {consumptionHistory &&
          Array.isArray(consumptionHistory.diesel_data) &&
          consumptionHistory.diesel_data.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={consumptionHistory.diesel_data}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 11 }}
                  tickFormatter={(value) =>
                    new Date(value).toLocaleDateString('es', {
                      month: 'short',
                      day: 'numeric'
                    })
                  }
                />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip
                  labelFormatter={(value) => new Date(value).toLocaleDateString()}
                  formatter={(value) => [`${value} L`, 'Diésel']}
                />
                <Line
                  type="monotone"
                  dataKey="quantity"
                  stroke="#F97316"
                  strokeWidth={3}
                  dot={{ fill: '#F97316', r: 3 }}
                  activeDot={{ r: 5 }}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[280px] flex items-center justify-center text-slate-400">
              <div className="text-center">
                <Fuel size={40} className="mx-auto mb-3 text-slate-300" />
                <p className="text-sm">Sin datos de consumo en este período</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* PRODUCTIVIDAD + COSTOS */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

        {/* HORAS Y KILÓMETROS */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">

          {/* HORAS */}
          <div
            className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm"
            data-testid="hours-worked-chart"
          >
            <div className="flex items-start justify-between mb-2">
              <div>
                <h3 className="text-lg font-bold text-slate-800">Horas trabajadas</h3>
                <p className="text-xs text-slate-500 mt-1">
                  Maquinaria registrada mediante horómetro.
                </p>
              </div>
              <div className="p-2 bg-blue-50 rounded-lg text-blue-600">
                <TrendingUp size={19} />
              </div>
            </div>

            {hoursHistory && hoursHistory.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={hoursHistory}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 11 }}
                    tickFormatter={(value) =>
                      new Date(value).toLocaleDateString('es', {
                        month: 'short',
                        day: 'numeric'
                      })
                    }
                  />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip
                    labelFormatter={(value) => new Date(value).toLocaleDateString()}
                    formatter={(value) => [
                      `${Number(value).toFixed(1)} h`,
                      'Horas'
                    ]}
                  />
                  <Bar dataKey="hours" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-slate-400">
                <div className="text-center">
                  <TrendingUp size={40} className="mx-auto mb-3 text-slate-300" />
                  <p className="text-sm">Sin datos de horas en este período</p>
                </div>
              </div>
            )}
          </div>

          {/* KILÓMETROS */}
          <div
            className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm"
            data-testid="kilometers-worked-chart"
          >
            <div className="flex items-start justify-between mb-2">
              <div>
                <h3 className="text-lg font-bold text-slate-800">Kilómetros recorridos</h3>
                <p className="text-xs text-slate-500 mt-1">
                  Recorrido registrado por las volquetas.
                </p>
              </div>
              <div className="p-2 bg-slate-100 rounded-lg text-slate-700">
                <Truck size={19} />
              </div>
            </div>

            {kilometersHistory && kilometersHistory.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={kilometersHistory}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 11 }}
                    tickFormatter={(value) =>
                      new Date(value).toLocaleDateString('es', {
                        month: 'short',
                        day: 'numeric'
                      })
                    }
                  />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip
                    labelFormatter={(value) => new Date(value).toLocaleDateString()}
                    formatter={(value) => [
                      `${Number(value).toFixed(1)} km`,
                      'Kilómetros'
                    ]}
                  />
                  <Bar dataKey="kilometers" fill="#64748B" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-slate-400">
                <div className="text-center">
                  <Truck size={40} className="mx-auto mb-3 text-slate-300" />
                  <p className="text-sm">Sin datos de kilómetros en este período</p>
                </div>
              </div>
            )}
          </div>

        </div>

        {/* COSTOS */}
        <div
          className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm"
          data-testid="cost-chart"
        >
          <div className="flex items-start justify-between mb-2">
            <div>
              <h3 className="text-lg font-bold text-slate-800">Gasto en combustible</h3>
              <p className="text-xs text-slate-500 mt-1">
                Evolución del gasto de Diésel y Gasolina en el período seleccionado.
              </p>
            </div>
            <div className="p-2 bg-red-50 rounded-lg text-red-600">
              <DollarSign size={19} />
            </div>
          </div>

          {fuelCostHistory &&
          Array.isArray(fuelCostHistory.data) &&
          fuelCostHistory.data.length > 0 ? (
            <>
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="rounded-lg border border-orange-100 bg-orange-50 p-3">
                  <p className="text-xs text-orange-700 font-medium">Diésel</p>
                  <p className="text-lg font-bold text-orange-800">
                    Bs. {Number(fuelCostHistory.diesel_cost || 0).toFixed(2)}
                  </p>
                </div>

                <div className="rounded-lg border border-blue-100 bg-blue-50 p-3">
                  <p className="text-xs text-blue-700 font-medium">Gasolina</p>
                  <p className="text-lg font-bold text-blue-800">
                    Bs. {Number(fuelCostHistory.gasoline_cost || 0).toFixed(2)}
                  </p>
                </div>

                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                  <p className="text-xs text-slate-600 font-medium">Total período</p>
                  <p className="text-lg font-bold text-slate-800">
                    Bs. {Number(fuelCostHistory.total_cost || 0).toFixed(2)}
                  </p>
                </div>
              </div>

              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={fuelCostHistory.data}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 11 }}
                    tickFormatter={(value) => {
                      if (String(value).length === 7) {
                        const [year, month] = String(value).split('-');
                        return new Date(
                          Number(year),
                          Number(month) - 1,
                          1
                        ).toLocaleDateString('es', {
                          month: 'short'
                        });
                      }

                      return new Date(value).toLocaleDateString('es', {
                        month: 'short',
                        day: 'numeric'
                      });
                    }}
                  />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip
                    labelFormatter={(value) => {
                      if (String(value).length === 7) {
                        const [year, month] = String(value).split('-');
                        return new Date(
                          Number(year),
                          Number(month) - 1,
                          1
                        ).toLocaleDateString('es', {
                          month: 'long',
                          year: 'numeric'
                        });
                      }

                      return new Date(value).toLocaleDateString('es-BO');
                    }}
                    formatter={(value, name) => [
                      `Bs. ${Number(value).toFixed(2)}`,
                      name === 'diesel'
                        ? 'Diésel'
                        : name === 'gasoline'
                          ? 'Gasolina'
                          : 'Total'
                    ]}
                  />
                  <Legend
                    formatter={(value) =>
                      value === 'diesel'
                        ? 'Diésel'
                        : value === 'gasoline'
                          ? 'Gasolina'
                          : 'Total'
                    }
                  />
                  <Line
                    type="monotone"
                    dataKey="diesel"
                    stroke="#F97316"
                    strokeWidth={2.5}
                    dot={{ r: 3 }}
                    activeDot={{ r: 5 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="gasoline"
                    stroke="#2563EB"
                    strokeWidth={2.5}
                    dot={{ r: 3 }}
                    activeDot={{ r: 5 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="total"
                    stroke="#475569"
                    strokeWidth={3}
                    dot={{ r: 3 }}
                    activeDot={{ r: 5 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </>
          ) : (
            <div className="h-[280px] flex items-center justify-center text-slate-400">
              <div className="text-center">
                <DollarSign size={30} className="mx-auto mb-2 opacity-40" />
                <p className="text-sm">Sin gastos registrados en el período.</p>
              </div>
            </div>
          )}
        </div>

      </div>

      {/* ALERTA OPERATIVA */}
      {(stats?.pending_maintenance || 0) > 0 || outOfService > 0 ? (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
          <div className="p-2 bg-amber-100 text-amber-700 rounded-lg shrink-0">
            <AlertTriangle size={19} />
          </div>
          <div>
            <p className="font-semibold text-amber-900">Atención operativa</p>
            <p className="text-sm text-amber-800 mt-1">
              Hay {outOfService} máquina{outOfService !== 1 ? 's' : ''} fuera de servicio
              {stats?.pending_maintenance
                ? ` y ${stats.pending_maintenance} mantenimiento${stats.pending_maintenance !== 1 ? 's' : ''} pendiente${stats.pending_maintenance !== 1 ? 's' : ''}.`
                : '.'}
            </p>
          </div>
        </div>
      ) : (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3">
          <div className="p-2 bg-green-100 text-green-700 rounded-lg shrink-0">
            <TrendingUp size={19} />
          </div>
          <div>
            <p className="font-semibold text-green-900">Operación estable</p>
            <p className="text-sm text-green-800 mt-1">
              No hay mantenimientos pendientes ni maquinaria fuera de servicio.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
